import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Avatar,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Box,
  IconButton,
  Link,
  Tooltip,
  CircularProgress,
  Divider,
  TextField
} from '@mui/material';
import {
  Edit as EditIcon,
  Business,
  Work,
  Email,
  Phone,
  Download as DownloadIcon,
  Lock as LockIcon,
  Language,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Chat as ChatIcon,
  Message,
  Link as LinkIcon,
  ContactPage
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, migrateToSmartEcoCard, createUserProfile } from '../../services/userProfileService';
import logger from '../../utils/logger';
import { QRCodeSVG } from 'qrcode.react';
import './Profile.css';

const SOCIAL_LINK_TYPES = {
  instagram: {
    name: "Let's Connect",
    description: "Follow me on Instagram",
    icon: <InstagramIcon />,
  },
  facebook: {
    name: "Let's Connect",
    description: "Follow me on Facebook",
    icon: <FacebookIcon />,
  },
  linkedin: {
    name: "Let's Connect",
    description: "Connect with me on LinkedIn",
    icon: <LinkedInIcon />,
  },
  skype: {
    name: "Let's Chat",
    description: "Chat with me on Skype",
    icon: <ChatIcon />,
  },
  telegram: {
    name: "Let's Chat",
    description: "Message me on Telegram",
    icon: <TelegramIcon />,
  },
  whatsapp: {
    name: "Let's Chat",
    description: "Message me on WhatsApp",
    icon: <WhatsAppIcon />,
    formatUrl: (url) => {
      // Clean the phone number to only keep digits and plus sign
      const phone = url.split('wa.me/').pop()?.split('/')[0] || '';
      return `https://wa.me/${phone}`;
    }
  },
  website: {
    name: "Company Website",
    description: "Visit our company website",
    icon: <Language />,
  }
};

const DEFAULT_PROFILE_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGNpcmNsZSBjeD0iMTAwIiBjeT0iMTAwIiByPSIxMDAiIGZpbGw9IiNFMEUwRTAiLz4KICA8Y2lyY2xlIGN4PSIxMDAiIGN5PSI4NSIgcj0iMzAiIGZpbGw9IiM5RTlFOUUiLz4KICA8cGF0aCBkPSJNMTY1IDE2NS41QzE2NSAxNjUuNSAxNTAgMTI1IDE0MCAxMjVINjBDNTAgMTI1IDM1IDE2NS41IDM1IDE2NS41QzM1IDE2NS41IDYwIDE4NSAxMDAgMTg1QzE0MCAxODUgMTY1IDE2NS41IDE2NSAxNjUuNVoiIGZpbGw9IiM5RTlFOUUiLz4KPC9zdmc+';

const Profile = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: currentUser?.displayName || '',
    email: currentUser?.email || '',
    role: '',
    company: '',
    phone: '',
    socialLinks: []
  });
  const qrRef = useRef();

  const isOwnProfile = currentUser && currentUser.uid === uid;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        // Run migration first
        if (isOwnProfile) {
          await migrateToSmartEcoCard();
        }
        const profile = await getUserProfile(uid);
        logger.info('Retrieved profile:', profile);
        if (profile) {
          logger.info('Setting user profile with name:', profile.name);
          setUserProfile(profile);
        } else if (isOwnProfile && currentUser) {
          logger.info('No profile found, redirecting to edit profile');
          navigate(`/profile/edit/${currentUser.uid}`);
        }
      } catch (error) {
        logger.error('Error fetching profile:', error);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (uid) {
      fetchProfile();
    }
  }, [uid, isOwnProfile, currentUser, navigate]);

  const handleEditClick = () => {
    setEditFormData({
      name: userProfile?.name || currentUser?.displayName || '',
      email: userProfile?.email || currentUser?.email || '',
      role: userProfile?.role || '',
      company: userProfile?.company || '',
      phone: userProfile?.phone || '',
      socialLinks: userProfile?.socialLinks || []
    });
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      await createUserProfile(currentUser.uid, {
        ...editFormData,
        photoURL: currentUser.photoURL,
        phonePrivacy: 'public',
        emailPrivacy: 'public'
      });
      
      // Refetch profile
      const updatedProfile = await getUserProfile(uid);
      setUserProfile(updatedProfile);
      setIsEditing(false);
    } catch (error) {
      logger.error('Error saving profile:', error);
      setError('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditProfile = () => {
    navigate(`/profile/edit/${currentUser.uid}`);
  };

  // Utility function to format phone number
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if it's a US number (10 digits)
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // Check if it's an international number with country code
    if (cleaned.length > 10) {
      // Assume first digit(s) before 10 digits is country code
      const countryCode = cleaned.slice(0, cleaned.length - 10);
      const areaCode = cleaned.slice(-10, -7);
      const middle = cleaned.slice(-7, -4);
      const last = cleaned.slice(-4);
      return `+${countryCode} (${areaCode}) ${middle}-${last}`;
    }
    
    // If it doesn't match expected formats, return original with any plus sign
    return phoneNumber.includes('+') ? phoneNumber : `+${phoneNumber}`;
  };

  // Helper function to check if a field should be visible
  const isFieldVisible = (field, profile) => {
    return profile[field + 'Privacy'] !== 'private' || isOwnProfile;
  };

  const handleDownloadQR = () => {
    const qrBox = qrRef.current;
    if (!qrBox) return;
    
    const svg = qrBox.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      // Set canvas size to be slightly larger than the QR code
      canvas.width = img.width + 40; // 20px padding on each side
      canvas.height = img.height + 40;
      
      // Fill white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code in the center
      ctx.drawImage(img, 20, 20);
      
      // Convert to PNG and download
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `${userProfile?.name || 'qr-code'}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleSaveContact = () => {
    // Create vCard content
    const fileName = (userProfile.name || userProfile.displayName || 'contact').replace(/\s+/g, '_');
    
    let vCardContent = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `N:${userProfile.name || userProfile.displayName};;;;`,
      `FN:${userProfile.name || userProfile.displayName}`,
      // Only include email if it's public
      isFieldVisible('email', userProfile) && userProfile.email && `EMAIL;TYPE=INTERNET:${userProfile.email}`,
      // Only include phone if it's public
      isFieldVisible('phone', userProfile) && userProfile.phone && `TEL;TYPE=CELL:${userProfile.phone}`,
      userProfile.company && `ORG:${userProfile.company}`,
      userProfile.role && `TITLE:${userProfile.role}`,
      userProfile.companyWebsite && `URL:${userProfile.companyWebsite}`,
      // Add Digital Business Card URL
      `URL;TYPE=home:${window.location.origin}/profile/${uid}`,
      // Add profile photo if it exists and is a data URL
      userProfile.photoURL?.startsWith('data:image/') && 
        `PHOTO;ENCODING=b;TYPE=${userProfile.photoURL.split(';')[0].split('/')[1]}:${userProfile.photoURL.split(',')[1]}`
    ].filter(Boolean);

    vCardContent.push('END:VCARD');

    // Create and download the vCard file
    const vCardBlob = new Blob([vCardContent.join('\n')], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(vCardBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.vcf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSocialLinkClick = (link) => {
    const linkType = SOCIAL_LINK_TYPES[link.platform];
    const url = linkType?.formatUrl ? linkType.formatUrl(link.url) : link.url;
    window.open(url, '_blank');
  };

  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'instagram':
        return <InstagramIcon />;
      case 'facebook':
        return <FacebookIcon />;
      case 'linkedin':
        return <LinkedInIcon />;
      case 'skype':
        return <ChatIcon />;
      case 'telegram':
        return <TelegramIcon />;
      case 'whatsapp':
        return <WhatsAppIcon />;
      default:
        return <LinkIcon />;
    }
  };

  return (
    <Container className="profile-container">
      <Paper className="profile-paper" elevation={3}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !userProfile && !isOwnProfile ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
            <Typography variant="h6">Profile Not Found</Typography>
            <Typography color="textSecondary">
              The requested profile could not be found.
            </Typography>
          </Box>
        ) : isEditing ? (
          <Box component="form" sx={{ mt: 2 }} onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }}>
            <Avatar
              src={currentUser?.photoURL}
              alt={editFormData.name}
              sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={editFormData.name}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={editFormData.email}
              onChange={handleInputChange}
              margin="normal"
              type="email"
              required
            />
            
            <TextField
              fullWidth
              label="Role"
              name="role"
              value={editFormData.role}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Company"
              name="company"
              value={editFormData.company}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={editFormData.phone}
              onChange={handleInputChange}
              margin="normal"
            />
            
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setIsEditing(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
              >
                Save Profile
              </Button>
            </Box>
          </Box>
        ) : !userProfile && isOwnProfile ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={3} p={4}>
            <Typography variant="h6">Welcome to SmartEcoCard!</Typography>
            <Typography color="textSecondary" align="center">
              Create your digital business card to start connecting with others.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleEditClick}
              startIcon={<EditIcon />}
            >
              Create Your Profile
            </Button>
          </Box>
        ) : (
          <>
            <Box className="profile-header">
              <Box className="avatar-container">
                <Avatar
                  src={userProfile.photoURL || DEFAULT_PROFILE_IMAGE}
                  alt={userProfile.name}
                  className="profile-avatar"
                />
                {isOwnProfile && (
                  <IconButton
                    onClick={handleEditProfile}
                    className="edit-button"
                    size="small"
                    aria-label="edit profile"
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
              <Typography variant="h4" className="profile-name">
                {userProfile.name || userProfile.displayName || currentUser?.displayName || 'User'}
              </Typography>
              <Button
                onClick={handleSaveContact}
                className="save-contact-button"
                startIcon={<ContactPage />}
              >
                Save to Contacts
              </Button>
            </Box>

            <Box className="contact-info">
              <List>
                {userProfile.company && (
                  <ListItem>
                    <ListItemIcon>
                      <Business />
                    </ListItemIcon>
                    <ListItemText
                      primary="Company"
                      secondary={userProfile.company}
                    />
                  </ListItem>
                )}
                {userProfile.role && (
                  <ListItem>
                    <ListItemIcon>
                      <Work />
                    </ListItemIcon>
                    <ListItemText
                      primary="Role"
                      secondary={userProfile.role}
                    />
                  </ListItem>
                )}
                {isFieldVisible('email', userProfile) && (
                  <ListItem>
                    <ListItemIcon>
                      <Email />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={
                        <Typography
                          component="div"
                          variant="body2"
                          className="private-field"
                        >
                          {userProfile.email}
                          {userProfile.emailPrivacy === 'private' && isOwnProfile && (
                            <Tooltip title="This field is private">
                              <LockIcon className="lock-icon" />
                            </Tooltip>
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                {isFieldVisible('phone', userProfile) && (
                  <ListItem>
                    <ListItemIcon>
                      <Phone />
                    </ListItemIcon>
                    <ListItemText
                      primary="Phone"
                      secondary={
                        <Typography
                          component="div"
                          variant="body2"
                          className="private-field"
                        >
                          {formatPhoneNumber(userProfile.phone)}
                          {userProfile.phonePrivacy === 'private' && isOwnProfile && (
                            <Tooltip title="This field is private">
                              <LockIcon className="lock-icon" />
                            </Tooltip>
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                {userProfile.companyWebsite && (
                  <ListItem>
                    <ListItemIcon>
                      <Language />
                    </ListItemIcon>
                    <ListItemText
                      primary="Website"
                      secondary={
                        <Link
                          href={userProfile.companyWebsite}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {userProfile.companyWebsite}
                        </Link>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>

            {userProfile.socialLinks?.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box className="social-links">
                  {userProfile.socialLinks.map((link, index) => (
                    <Link
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link-item"
                    >
                      {getSocialIcon(link.platform)}
                      <Typography>{link.platform}</Typography>
                    </Link>
                  ))}
                </Box>
              </>
            )}

            <Box className="action-buttons">
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadQR}
              >
                Download QR
              </Button>
            </Box>

            <Box className="qr-code-section">
              <Typography variant="h6" gutterBottom>
                Scan QR Code
              </Typography>
              <Box className="qr-code" ref={qrRef}>
                <QRCodeSVG
                  value={`${window.location.origin}/profile/${uid}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Profile;
